import { create } from 'zustand';
import { ApiClient } from '@/lib/api-client';
import { Quest, Element } from '@/constant/schema';
import yaml from 'yaml';
import { ELEMENT_TYPE_DESCRIPTIONS } from '@/app/api/llm-teacher/prompts';

export interface LLMMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  type: 'text' | 'element';
  content?: string;
  element?: Element;
  timestamp: string;
}

interface LLMTeacherStore {
  // 消息列表 - 包含所有消息（文本和元素）
  messages: LLMMessage[];
  
  // 当前学习上下文
  questInfo: {
    questId: string;
    questTitle: string;
    chapterId: string;
    chapterTitle: string;
    lessonId: string;
    lessonTitle: string;
  } | null;
  
  // 状态
  isLoading: boolean;
  error: string | null;
  
  // 初始化
  initialize: (quest: Quest, chapterId: string, lessonId: string) => void;
  
  // 从历史记录重建消息
  loadFromHistory: (historicalMessages: any[]) => void;
  
  // 添加消息
  addUserMessage: (content: string) => void;
  addAssistantMessage: (content: string) => void;
  addElementMessage: (element: Element) => void;
  
  // 发送消息到 LLM
  sendToLLM: (userInput: string, currentElement?: Element) => Promise<string>;
  
  // 清理
  clear: () => void;
  
  // 内部方法
  convertMessagesForAPI: () => any[];
}

const useLLMTeacherStore = create<LLMTeacherStore>((set, get) => ({
  messages: [],
  questInfo: null,
  isLoading: false,
  error: null,

  initialize: (quest: Quest, chapterId: string, lessonId: string) => {
    const chapter = quest.chapters?.find(ch => ch.id === chapterId);
    const lesson = chapter?.lesson_list?.find(l => l.id === lessonId);
    
    if (!chapter || !lesson) {
      set({ error: 'Chapter or lesson not found' });
      return;
    }

    set({
      questInfo: {
        questId: quest.id,
        questTitle: quest.title,
        chapterId: chapter.id,
        chapterTitle: chapter.title,
        lessonId: lesson.id,
        lessonTitle: lesson.title,
      },
      messages: [],
      error: null
    });

    // 只有在没有历史记录时才添加欢迎消息和第一个元素
    // 这将由 loadFromHistory 或调用方决定
  },

  loadFromHistory: (historicalMessages: any[]) => {
    const llmMessages: LLMMessage[] = [];
    
    for (const msg of historicalMessages) {
      if (msg.type === 'text') {
        // 文本消息
        llmMessages.push({
          id: msg.id || `msg-${Date.now()}-${Math.random()}`,
          role: msg.role as 'user' | 'assistant',
          type: 'text',
          content: msg.content || '',
          timestamp: msg.created_at || new Date().toISOString()
        });
      } else if (msg.type === 'element' && msg.element) {
        // 元素消息
        llmMessages.push({
          id: msg.id || `msg-${Date.now()}-${Math.random()}`,
          role: 'user',  // 元素总是 user 角色
          type: 'element',
          element: msg.element,
          timestamp: msg.created_at || new Date().toISOString()
        });
      }
    }
    
    set({ messages: llmMessages });
  },

  addUserMessage: (content: string) => {
    set(state => ({
      messages: [...state.messages, {
        id: `msg-${Date.now()}`,
        role: 'user',
        type: 'text',
        content,
        timestamp: new Date().toISOString()
      }]
    }));
  },

  addAssistantMessage: (content: string) => {
    set(state => ({
      messages: [...state.messages, {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        type: 'text',
        content,
        timestamp: new Date().toISOString()
      }]
    }));
  },

  addElementMessage: (element: Element) => {
    set(state => ({
      messages: [...state.messages, {
        id: `msg-${Date.now()}`,
        role: 'user',  // Elements are now user messages
        type: 'element',
        element,
        timestamp: new Date().toISOString()
      }]
    }));
  },

  convertMessagesForAPI: () => {
    const messages = get().messages;
    let previousElementTitle: string | null = null;
    
    return messages.map((msg, index) => {
      if (msg.type === 'text') {
        // 文本消息保持不变
        return {
          role: msg.role,
          content: msg.content
        };
      } else if (msg.type === 'element' && msg.element) {
        // 元素转换为 YAML 格式的用户消息
        const element = msg.element;
        
        // 使用从 prompts 导入的元素类型描述
        
        // 创建一个清理后的对象，去除空值和undefined
        const cleanElement: any = {};

        // 添加元素描述
        cleanElement._element_description = ELEMENT_TYPE_DESCRIPTIONS[element.element_type] ||
          `This is a ${element.element_type} learning element`;

        // 定义要排除的字段（ID和时间戳）
        const excludedFields = new Set([
          'id',
          'lesson_id',
          'element_type',
          'created_at',
          'updated_at',
          'chapter_id',
          'quest_id',
          'user_id'
        ]);

        // 遍历element的所有属性，只保留非空值且不在排除列表中的字段
        for (const [key, value] of Object.entries(element)) {
          // 排除ID字段、时间戳字段，以及所有以_id结尾的字段
          if (excludedFields.has(key) || key.endsWith('_id')) {
            continue;
          }

          if (value !== null && value !== undefined && value !== '' &&
              !(Array.isArray(value) && value.length === 0) &&
              !(typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)) {
            cleanElement[key] = value;
          }
        }

        // 如果有用户交互上下文，确保它被包含
        if ((element as any)._context && Object.keys((element as any)._context).length > 0) {
          cleanElement._user_interaction_context = (element as any)._context;
        }

        // 转换为 YAML - 只包含非空数据
        const yamlContent = yaml.stringify(cleanElement, {
          nullStr: ''
        });
        
        // 构建学习进度消息
        let progressMessage = '';
        for (let i = index - 1; i >= 0; i--) {
          if (messages[i].type === 'element' && messages[i].element) {
            previousElementTitle = messages[i].element?.title || 'the previous element';
            progressMessage = `The student has completed learning "${previousElementTitle}" and is now progressing to the next learning element.\n\n`;
            break;
          }
        }
        
        // 如果是第一个元素
        if (!progressMessage && index === messages.findIndex(m => m.type === 'element')) {
          progressMessage = 'The student is beginning the lesson with the following learning element.\n\n';
        }
        
        // 构建完整的元素介绍
        const elementIntroduction = `${progressMessage}Please help the student understand and engage with the following learning element. Pay attention to its type and content to provide appropriate guidance:\n\n`;
        
        return {
          role: 'user',  // Elements are user messages
          content: `${elementIntroduction}[Learning Element Details]\n\`\`\`yaml\n${yamlContent}\`\`\``
        };
      }
      
      return null;
    }).filter(Boolean);
  },

  sendToLLM: async (userInput: string, currentElement?: Element) => {
    const { questInfo } = get();
    
    if (!questInfo) {
      set({ error: 'No quest context available' });
      return '';
    }

    set({ isLoading: true, error: null });

    // 添加用户消息
    get().addUserMessage(userInput);

    try {
      // 如果有当前元素的上下文，更新最后一个元素消息
      if (currentElement) {
        const messages = get().messages;
        const lastElementIndex = messages.findLastIndex(m => 
          m.type === 'element' && m.element?.id === currentElement.id
        );
        
        if (lastElementIndex >= 0) {
          // 更新元素的上下文信息
          const updatedMessages = [...messages];
          updatedMessages[lastElementIndex] = {
            ...updatedMessages[lastElementIndex],
            element: currentElement
          };
          set({ messages: updatedMessages });
        }
      }

      // 转换消息格式
      const apiMessages = get().convertMessagesForAPI();

      // 调用 API
      const response = await ApiClient.post('/api/llm-teacher', {
        questInfo,
        messages: apiMessages,
        currentElement: currentElement ? {
          type: currentElement.element_type,
          title: currentElement.title
        } : null
      });

      const data = await response.json();
      
      if (data.response) {
        // 添加助手回复
        get().addAssistantMessage(data.response);
        set({ isLoading: false });
        return data.response;
      }
      
      throw new Error('No response from LLM');
    } catch (error) {
      console.error('Failed to get LLM response:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to get response',
        isLoading: false
      });
      return '';
    }
  },

  clear: () => {
    set({
      messages: [],
      questInfo: null,
      isLoading: false,
      error: null
    });
  }
}));

export default useLLMTeacherStore;